import GiftCard1 from 'components/GiftCard1';
import { FormattedMessage, useIntl } from '@alltrails/shared/react-intl';
import { ReactNode } from 'react';
import * as styles from './GiftValuePanel.module.scss';

type ValueBlurbProps = {
  title: ReactNode;
  content: ReactNode;
};

const ValueBlurb = ({ title, content }: ValueBlurbProps) => (
  <div className={styles.subHeadAndSubText}>
    <h4 className={styles.subHeader}>{title}</h4>
    <div className={styles.subText}>{content}</div>
  </div>
);

const GiveJoy = () => (
  <section>
    <div className={styles.headerAndSubText}>
      <h2 className={styles.header}>
        <FormattedMessage defaultMessage="An easy way to give joy" />
      </h2>
      <div className={styles.subText}>
        <FormattedMessage defaultMessage="Send the gift of exploration to your friends and family with an AllTrails Pro gift subscription." />
      </div>
    </div>
    <div className={styles.imageAndBlurbs}>
      <div>
        <div className={styles.gc}>
          <GiftCard1 />
        </div>
      </div>
      <div className={styles.blurbs}>
        <ValueBlurb
          title={<FormattedMessage defaultMessage="Quick and easy" />}
          content={<FormattedMessage defaultMessage="Purchase a gift subscription easily. It arrives in moments and we notify you of delivery." />}
        />
        <ValueBlurb
          title={<FormattedMessage defaultMessage="Customizable" />}
          content={<FormattedMessage defaultMessage="Choose the length of plan, a design you love and personalize a message." />}
        />
        <ValueBlurb
          title={<FormattedMessage defaultMessage="Send via email or print" />}
          content={<FormattedMessage defaultMessage="Choose the delivery date the gift subscription will be emailed. Or, print and hand-deliver." />}
        />
      </div>
    </div>
  </section>
);

const PerfectForAll = () => {
  const intl = useIntl();

  return (
    <section className={styles.perfectForAllContainer}>
      <div className={styles.headerAndSubText}>
        <h2 className={styles.header}>
          <FormattedMessage defaultMessage="Perfect for all occasions and outdoor enthusiasts." />
        </h2>
        <div className={styles.subText}>
          <FormattedMessage defaultMessage="Celebrate the people you appreciate on birthdays, holidays, graduations and more. With the ability to browse by activity, it’s a fitting gift for any type of outdoor explorer in your life." />
        </div>
      </div>
      <div className={styles.imageGrid}>
        <div className={styles.leftImageColumn}>
          <img
            className={`${styles.largeImage} lazyload`}
            data-src="https://cdn-assets.alltrails.com/assets/images/gifting/image-a@2x.png"
            alt={intl.formatMessage({ defaultMessage: 'Friends relaxing at a rocky summit' })}
          />
          <div className={styles.subImages}>
            <img
              className={`${styles.imageA} lazyload`}
              data-src="https://cdn-assets.alltrails.com/assets/images/gifting/image-b@2x.png"
              alt={intl.formatMessage({ defaultMessage: 'Two cyclists walking their bikes across a wooden bridge' })}
            />
            <img
              className={`${styles.imageA} lazyload`}
              data-src="https://cdn-assets.alltrails.com/assets/images/gifting/image-c@2x.png"
              alt={intl.formatMessage({ defaultMessage: 'Person trail running on a beautiful sunny day' })}
            />
          </div>
        </div>
        <div className={styles.rightImageColumn}>
          <img
            className={`${styles.imageC} lazyload`}
            data-src="https://cdn-assets.alltrails.com/assets/images/gifting/image-d@2x.png"
            alt={intl.formatMessage({ defaultMessage: 'Person cycling up a long and windy hill' })}
          />
          <div className={styles.athleteList}>
            <div>
              <FormattedMessage defaultMessage="Hikers" />
            </div>
            <div>
              <FormattedMessage defaultMessage="Mountain bikers" />
            </div>
            <div>
              <FormattedMessage defaultMessage="Trail runners" />
            </div>
            <div>
              <FormattedMessage defaultMessage="Backpackers" />
            </div>
            <div>
              <FormattedMessage defaultMessage="Road bikers" />
            </div>
            <div>
              <FormattedMessage defaultMessage="Rock climbers" />
            </div>
            <div>
              <FormattedMessage defaultMessage="Snowshoers" />
            </div>
            <div>
              <FormattedMessage defaultMessage="Skiers" />
            </div>
            <div className={styles.andMore}>
              <FormattedMessage defaultMessage="and more" />
              ...
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const GiftValuePanel = () => (
  <div className={styles.container}>
    <GiveJoy />
    <PerfectForAll />
  </div>
);

export default GiftValuePanel;
